/* eslint-disable react/jsx-no-constructed-context-values */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */

import React, {createContext, useCallback, useContext, useEffect, useReducer} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {
  ArmSortOrderValue,
  BASE_WORLD_LIST_SORT_ORDERS,
  BaseWorldListSortOrderValue,
  OPERATING_TEST_URLS,
  WORK_SORT_ORDER,
  WorkSortOrderValue
} from "../../../constants/constants";
import {FindWorkResponseData} from "../../../utils/awsAmplifyUtil";
import {Status, STATUSES} from "../../../hooks/useFetchAllQuery";
import {SearchCond} from "../components/forms/SearchForm";
import {CalcHandCandidatePoolResult, FindModelResponse, HandResultItem} from "../../../types/pj1dbApiHandling";
import {WorkClassDocument, WorkInstanceDocument} from "../../../types/pj1dbApiWork";
import {HandPowerSourceFieldInputs} from "../../handselection/components/forms/HandPowerSourceField";
import {ArmDocument} from "../../equipmentdb/types/pj1dbApiArm";
import {Dict} from "../../equipmentdb/types/pj1dbApi";
import {
  initialSearchCriteriaGroupInputs,
  SearchCriteriaInstanceGroupInputs
} from "../components/forms/SearchCriteriaInstanceGroup";
import {CALCULATED_SORT_ORDER, CalculatedSortOrder} from "../../../constants/handselection";
import {HandDocument} from "../../equipmentdb/types/pj1dbApiHand";
import {FingertipDocument} from "../../equipmentdb/types/pj1dbApiFingertip";
import {AttachmentDocument} from "../../equipmentdb/types/pj1dbApiAttachment";
import {
  BaseWorldDocument,
  CustomWorldDocument,
  FindBaseWorldResponse,
  FindCustomWorldResponse
} from "../../../types/pj1dbApiWorld";

export const STEPS = {
  WORK_CLASS_SELECTION: 'WORK_CLASS_SELECTION',
  WORK_INSTANCE_SELECTION: 'WORK_INSTANCE_SELECTION',
  SEARCH_CRITERIA_ADDITIONAL: 'SEARCH_CRITERIA_ADDITIONAL',
  EQUIPMENT_CONFIGURATION: 'EQUIPMENT_CONFIGURATION',
  BASE_WORLD_SELECTION: 'BASE_WORLD_SELECTION',
  CUSTOM_WORLD_SELECTION: 'CUSTOM_WORLD_SELECTION',
  SIMULATION_SETTINGS: 'SIMULATION_SETTINGS',
} as const;
export type Step = typeof STEPS[keyof typeof STEPS];

export const STEP_ORDERS = {
  [STEPS.WORK_CLASS_SELECTION]: {
    PREV: null,
    NEXT: STEPS.WORK_INSTANCE_SELECTION,
  },
  [STEPS.WORK_INSTANCE_SELECTION]: {
    PREV: STEPS.WORK_CLASS_SELECTION,
    NEXT: STEPS.SEARCH_CRITERIA_ADDITIONAL,
  },
  [STEPS.SEARCH_CRITERIA_ADDITIONAL]: {
    PREV: STEPS.WORK_INSTANCE_SELECTION,
    NEXT: STEPS.EQUIPMENT_CONFIGURATION,
  },
  [STEPS.EQUIPMENT_CONFIGURATION]: {
    PREV: STEPS.SEARCH_CRITERIA_ADDITIONAL,
    NEXT: STEPS.BASE_WORLD_SELECTION,
  },
  [STEPS.BASE_WORLD_SELECTION]: {
    PREV: STEPS.EQUIPMENT_CONFIGURATION,
    NEXT: STEPS.CUSTOM_WORLD_SELECTION,
  },
  [STEPS.CUSTOM_WORLD_SELECTION]: {
    PREV: STEPS.BASE_WORLD_SELECTION,
    NEXT: STEPS.SIMULATION_SETTINGS,
  },
  [STEPS.SIMULATION_SETTINGS]: {
    PREV: STEPS.CUSTOM_WORLD_SELECTION,
    NEXT: null,
  },
} as const satisfies Record<Step, { PREV: Step | null, NEXT: Step | null }>;

export const STEP_OUTPUT_VALIDATION = {
  [STEPS.WORK_CLASS_SELECTION]: (state: DataStoreType) => state.workClassStep.selectedWorkClass !== null,
  [STEPS.WORK_INSTANCE_SELECTION]: (state: DataStoreType) => state.workInstanceStep.selectedWorkInstance !== null,
  [STEPS.SEARCH_CRITERIA_ADDITIONAL]: (state: DataStoreType) => state.searchCriteriaStep.searchCriteriaList.length > 0 &&
    state.searchCriteriaStep.selectedArm !== null,
  [STEPS.EQUIPMENT_CONFIGURATION]: (state: DataStoreType) => state.equipmentStep.selectedData !== null,
  [STEPS.BASE_WORLD_SELECTION]: (state: DataStoreType) => state.baseWorldStep.selectedBaseWorld !== null,
  [STEPS.CUSTOM_WORLD_SELECTION]: (state: DataStoreType) => state.customWorldStep.selectedCustomWorld !== null,
  [STEPS.SIMULATION_SETTINGS]: (state: DataStoreType) => true,
} as const satisfies Record<Step, (state: DataStoreType) => boolean>;

export function validateStepOutput(state: DataStoreType, step: Step): boolean {
  // 再起的に過去のステップのバリデーションを行う
  if (!STEP_OUTPUT_VALIDATION[step](state)) {
    return false;
  }

  const prev = STEP_ORDERS[step].PREV;
  if (prev !== null) {
    return validateStepOutput(state, prev);
  }

  return true;
}

export type SearchCriteriaInstance = {
  // "開時幅"
  rangeOpen: string;
  // "閉時幅の値"
  rangeClose: string;
  // "ストローク"
  stroke: string;
  // "把持力下限"
  forceMin: string;
  // "把持力上限"
  forceMax?: string;
  // "可搬重量"
  payload: string;
}

export type SearchCriteriaListItem = SearchCriteriaInstance & {
  // work class attr
  softMaterial: boolean;
  deformable: boolean;
  // work instance attr
  instanceID: string
  // work model attr
  direction: Required<FindModelResponse>['gravityWithDirections'][number];
  flexibleWidth?: string;
}

// useReducerで生成する「参照用のstate」の型
export type DataStoreType = {
  tabIndex: number;
  step: Step;
  workClassStep: {
    workClasses: FindWorkResponseData<'class'>[];
    workRepresentativeModels: FindModelResponse[];
    searchStatus: Status;
    searchCond: SearchCond<WorkSortOrderValue>;
    selectedWorkClass: WorkClassDocument | null;
  },
  workInstanceStep: {
    workInstances: FindWorkResponseData<'instance'>[];
    searchStatus: Status;
    selectedWorkInstance: WorkInstanceDocument | null;
  },
  searchCriteriaStep: {
    workModel: FindModelResponse | null;
    searchStatus: Status;
    selectedDirectionIndex: number;
    selectedObjectId: string | null;
    searchCriteria: SearchCriteriaInstanceGroupInputs;
    searchCriteriaList: SearchCriteriaListItem[]
    handPowerSourceInputs: HandPowerSourceFieldInputs;
    arms: Dict<ArmDocument>[];
    armSearchStatus: Status;
    armSearchCond: SearchCond<ArmSortOrderValue>;
    selectedArm: ArmDocument | null;
    calculationResults: CalcHandCandidatePoolResult | null;
  },
  equipmentStep: {
    searchStatus: Status;
    hands: Dict<HandDocument>[];
    fingertips: Dict<FingertipDocument>[];
    connectableAttachments: Dict<AttachmentDocument>[];
    sortOrder: CalculatedSortOrder;
    selectedData: {
      suggestionPattern: HandResultItem,
      hand: HandDocument,
      fingertip: FingertipDocument | null,
    } | null;
  },
  baseWorldStep: {
    baseWorlds: FindBaseWorldResponse[];
    searchCond: SearchCond<BaseWorldListSortOrderValue>
    searchStatus: Status;
    selectedBaseWorld: BaseWorldDocument | null;
  },
  customWorldStep: {
    searchText: string;
    customWorlds: FindCustomWorldResponse[];
    searchStatus: Status;
    selectedCustomWorld: CustomWorldDocument | null;
  },
  simulationStep: {
    inputs: {
      dummy: string;
    }
  },
};

export const initialState: DataStoreType = {
  tabIndex: 0,
  step: STEPS.WORK_CLASS_SELECTION,
  workClassStep: {
    workClasses: [],
    workRepresentativeModels: [],
    searchStatus: STATUSES.IDLE,
    searchCond: {
      sortOrder: WORK_SORT_ORDER.NAME.value,
      keyword: "",
      listCount: 0,
    },
    selectedWorkClass: null,
  },
  workInstanceStep: {
    workInstances: [],
    searchStatus: STATUSES.IDLE,
    selectedWorkInstance: null,
  },
  searchCriteriaStep: {
    workModel: null,
    searchStatus: STATUSES.IDLE,
    selectedDirectionIndex: 0,
    selectedObjectId: null,
    searchCriteria: initialSearchCriteriaGroupInputs,
    searchCriteriaList: [],
    handPowerSourceInputs: {
      handPowerSource: [],
    },
    arms: [],
    armSearchStatus: STATUSES.IDLE,
    armSearchCond: {
      sortOrder: WORK_SORT_ORDER.NAME.value,
      keyword: "",
      listCount: 0,
    },
    selectedArm: null,
    calculationResults: null,
  },
  equipmentStep: {
    searchStatus: STATUSES.IDLE,
    hands: [],
    fingertips: [],
    connectableAttachments: [],
    sortOrder: CALCULATED_SORT_ORDER.SCORE_TOTAL,
    selectedData: null,
  },
  baseWorldStep: {
    baseWorlds: [],
    searchCond: {
      sortOrder: BASE_WORLD_LIST_SORT_ORDERS.BASE_WORLD_NAME.value,
      keyword: "",
      listCount: 0,
    },
    searchStatus: STATUSES.IDLE,
    selectedBaseWorld: null,
  },
  customWorldStep: {
    searchText: '',
    customWorlds: [],
    searchStatus: STATUSES.IDLE,
    selectedCustomWorld: null,
  },
  simulationStep: {
    inputs: {
      dummy: "",
    },
  },
};

// dispatch関数の第2引数に渡す「action」の型
type ReducerActionType = {
  type: string;
  payload: any;
};

// createContext()のデフォルト値オブジェクトにasで割り当てる。
type DataStoreContextType = {
  state: DataStoreType;
  // dispatchの引数オブジェクトの型を、React.Dispatch<XXXXX> に定義する。
  dispatch: React.Dispatch<ReducerActionType>;
};

// reducer関数：更新用dispatchトリガーで、stateを更新する処理。
// 引数:   1.state 2.action(dispatch関数の引数)
// 戻り値: 更新後の新しいstate
export const CONTEXT_ACTION_TYPE = {
  RESET: "RESET",
  TAB: "SET_TAB_INDEX",
  STEP: "SET_STEP",
  WORK_CLASS: "SET_WORK_CLASS",
  WORK_INSTANCE: "SET_WORK_INSTANCE",
  SEARCH_CRITERIA: "SET_SEARCH_CRITERIA",
  EQUIPMENT_CONFIGURATION: "SET_EQUIPMENT_CONFIGURATION",
  BASE_WORLD: "SET_BASE_WORLD",
  CUSTOM_WORLD: "SET_CUSTOM_WORLD",
  SIMULATION_SETTINGS: "SET_SIMULATION_SETTINGS",
} as const;

const reducerFunc = (state: DataStoreType, action: ReducerActionType) => {
  // action.typeの値で更新内容を切り替える。
  switch (action.type) {
    case CONTEXT_ACTION_TYPE.RESET:
      return {
        ...initialState,
      };
    case CONTEXT_ACTION_TYPE.TAB:
      return {
        ...state,
        tabIndex: action.payload,
      } satisfies DataStoreType;
    case CONTEXT_ACTION_TYPE.STEP:
      return {
        ...state,
        step: action.payload,
      } satisfies DataStoreType;
    case CONTEXT_ACTION_TYPE.WORK_CLASS:
      return {
        ...state,
        workClassStep: {
          ...state.workClassStep,
          ...action.payload,
        }
      } satisfies DataStoreType;
    case CONTEXT_ACTION_TYPE.WORK_INSTANCE:
      return {
        ...state,
        workInstanceStep: {
          ...state.workInstanceStep,
          ...action.payload,
        }
      } satisfies DataStoreType;
    case CONTEXT_ACTION_TYPE.SEARCH_CRITERIA:
      return {
        ...state,
        searchCriteriaStep: {
          ...state.searchCriteriaStep,
          ...action.payload,
        }
      } satisfies DataStoreType;
    case CONTEXT_ACTION_TYPE.EQUIPMENT_CONFIGURATION:
      return {
        ...state,
        equipmentStep: {
          ...state.equipmentStep,
          ...action.payload,
        }
      } satisfies DataStoreType;
    case CONTEXT_ACTION_TYPE.BASE_WORLD:
      return {
        ...state,
        baseWorldStep: {
          ...state.baseWorldStep,
          ...action.payload,
        }
      } satisfies DataStoreType;
    case CONTEXT_ACTION_TYPE.CUSTOM_WORLD:
      return {
        ...state,
        customWorldStep: {
          ...state.customWorldStep,
          ...action.payload,
        }
      } satisfies DataStoreType;
    case CONTEXT_ACTION_TYPE.SIMULATION_SETTINGS:
      return {
        ...state,
        simulationStep: {
          ...state.simulationStep,
          ...action.payload,
        }
      } satisfies DataStoreType;
    // 更新前のstateをそのまま返す。
    default:
      return state;
  }
};

// createContextはReactフックではないため、コンポーネント外で使用可能
// as でオブジェクトの型チェックをクリアする。
export const DataStoreContext = createContext({} as DataStoreContextType);

export function DataStoreContextProvider(props: any): JSX.Element {
  // useReducerで生成した「参照用state」と「更新用dispatch」を、contextに渡す。
  const [state, dispatch] = useReducer(reducerFunc, initialState);
  return (
    <DataStoreContext.Provider
      value={{
        state,
        dispatch,
      }}
    >
      {props.children}
    </DataStoreContext.Provider>
  );
}

export function useStep(): [Step, (step: Step) => void] {
  const {state, dispatch} = useContext(DataStoreContext);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === OPERATING_TEST_URLS.SIMULATION.URL) {
      const newStep = location.state !== null &&
      location.state !== undefined &&
      'step' in location.state &&
      Object.values(STEPS).includes(location.state.step as Step)
        ? location.state.step
        : STEPS.WORK_CLASS_SELECTION;

      dispatch({
        type: CONTEXT_ACTION_TYPE.STEP,
        payload: newStep,
      });
    }
  }, [location, dispatch]);

  const setStep = useCallback((step: Step) => {
    navigate(OPERATING_TEST_URLS.SIMULATION.URL, {state: {step}});
    dispatch({type: CONTEXT_ACTION_TYPE.STEP, payload: step});
  }, [navigate, dispatch]);

  return [state.step, setStep];
}

export function useReset(): () => void {
  const {dispatch} = useContext(DataStoreContext);
  const navigate = useNavigate();
  return useCallback(() => {
    navigate(OPERATING_TEST_URLS.SIMULATION.URL, {state: {step: STEPS.WORK_CLASS_SELECTION}});
    dispatch({type: CONTEXT_ACTION_TYPE.RESET, payload: null});
  }, [navigate, dispatch]);
}

export type TabIndexPayload = DataStoreType['tabIndex'];
export type StepPayload = DataStoreType['step'];
export type WorkClassPayload = Partial<DataStoreType['workClassStep']>;
export type WorkInstancePayload = Partial<DataStoreType['workInstanceStep']>;
export type SearchCriteriaPayload = Partial<DataStoreType['searchCriteriaStep']>;
export type EquipmentConfigurationPayload = Partial<DataStoreType['equipmentStep']>;
export type BaseWorldPayload = Partial<DataStoreType['baseWorldStep']>;
export type CustomWorldPayload = Partial<DataStoreType['customWorldStep']>;
export type SimulationSettingsPayload = Partial<DataStoreType['simulationStep']>;
